import React, { useEffect, useState } from 'react';

import sortBy from 'lodash/sortBy';

import {
    Grid,
    Link,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox,
    FormControlLabel,
    Button,
    TextField,
    Autocomplete,
    Box,
    InputAdornment
} from '@mui/material';

import { SelectChangeEvent } from '@mui/material/Select';

import HelpTooltip from '../shared/HelpTooltip';
import { DiscProperties, EventFormData } from '../data_types/types';
import { DISC_PROPERTY_TYPES } from '../data/disc_data';
import { MOULD_NAMES } from '../data/disc_data';
import { DISC_COLORS } from '../data/disc_data';
import { DISC_STAMP_TYPES } from '../data/disc_data';
import { DISC_STAMP_NAMES } from '../data/disc_data';
import { REWARD_OPTIONS } from '../data/disc_data';
import { VALID_FIELD } from './AddEventForm';
import { Overline } from '../shared/Dashboard';

import { PlainObject } from '../data_types/types';

interface EventRewardsFormProps {
    formData: EventFormData;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => void;
    onValidationChange?: (isValid: PlainObject) => void;
}

// Stamp variant sign tells the game where the stamp lives: positive = built into the project, negative = Firebase storage.
// The Firebase field shows the "-" as a fixed prefix, so only the digits are edited here.
const firebaseStampToDraft = (value?: number) => (value && value < 0 ? String(-value) : '');
const draftToFirebaseStamp = (digits: string) => (digits ? -Number(digits) : 0);

const EventRewardsForm: React.FC<EventRewardsFormProps> = ({ formData, handleInputChange, onValidationChange }) => {
    const [error, setError] = useState<boolean>(false);
    const [customDiscError, setCustomDiscError] = useState<boolean>(false);
    const [selectedDiscProperties, setSelectedDiscProperties] = useState<number[]>(formData.customReward?.discProperties || []);

    const [stampType, setStampType] = useState<number>(formData.participationReward === 2 ? 2 : 0);

    useEffect(() => {
        // When custom disc is selected, set default stampType to FirebaseStamp (2)
        if (formData.participationReward === 2 && stampType !== 2) {
            setStampType(2);
        }
    }, [formData.participationReward, stampType]);

    const [firebaseStampDraft, setFirebaseStampDraft] = useState<string>(firebaseStampToDraft(formData.customReward?.stampVariant));

    useEffect(() => {
        const value = formData.customReward?.stampVariant ?? 0;
        setFirebaseStampDraft((draft) => (draftToFirebaseStamp(draft) === value ? draft : firebaseStampToDraft(value)));
    }, [formData.customReward?.stampVariant]);

    useEffect(() => {
        // Validate participation reward
        if (formData.participationReward === undefined) {
            setError(true);

            if (onValidationChange) {
                onValidationChange({
                    participationReward: 'You must select a valid reward option'
                });
            }
        } else {
            setError(false);
            if (formData.participationReward !== 2) {
                setCustomDiscError(false);
            }
            if (onValidationChange) {
                onValidationChange({
                    participationReward: VALID_FIELD
                });
            }
        }

        // If 'Custom Disc' is selected, validate customReward
        if (formData.participationReward === 2) {
            if (formData.customReward?.colorsName.trim() === '' || selectedDiscProperties.length === 0) {
                setCustomDiscError(true);
                if (onValidationChange) {
                    onValidationChange({
                        customDiscError: 'Custom disc must be valid'
                    });
                }
            } else {
                setCustomDiscError(false);
                if (onValidationChange) {
                    onValidationChange({
                        customDiscError: VALID_FIELD
                    });
                }
            }
        }
    }, [formData.participationReward, formData.customReward, selectedDiscProperties, onValidationChange]);

    const handleRewardsChange = (event: SelectChangeEvent<number | string>) => {
        const value = event.target.value;
        const numericValue = typeof value === 'string' && value !== '' ? Number(value) : value;

        handleInputChange({
            target: {
                name: 'participationReward',
                value: numericValue === 0 ? 0 : numericValue,
            },
        } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleCustomDiscChange = (property: keyof DiscProperties, value: any) => {
        const updatedCustomReward: DiscProperties = {
            ...formData.customReward!,
            [property]: value,
        };

        const customReward = {
            target: {
                name: 'customReward',
                value: updatedCustomReward,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        handleInputChange(customReward);
    };

    const handleDiscPropertyChange = (index: number, value: number | string) => {
        let newDiscProperties = [...selectedDiscProperties];
        if (value === '') return;

        const numericValue = typeof value === 'string' ? Number(value) : value;

        newDiscProperties[index] = numericValue;
        setSelectedDiscProperties(newDiscProperties);
        handleCustomDiscChange('discProperties', newDiscProperties);
    };

    const addDiscProperty = () => {
        if (selectedDiscProperties.length < 2) {
            setSelectedDiscProperties([...selectedDiscProperties, 0]);
        }
    };

    const removeDiscProperty = () => {
        if (selectedDiscProperties.length > 0) {
            const newDiscProperties = selectedDiscProperties.slice(0, -1);
            setSelectedDiscProperties(newDiscProperties);
            handleCustomDiscChange('discProperties', newDiscProperties);
        }
    };

    const sortedMouldNames = sortBy(MOULD_NAMES, 'label');
    const sortedDiscPropertyTypes = sortBy(DISC_PROPERTY_TYPES, 'label');
    const sortedDiscColors = sortBy(DISC_COLORS);
    const sortedDiscStampNames = sortBy(DISC_STAMP_NAMES, 'label');

    return (
        <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControl fullWidth required error={error}>
                        <InputLabel id="event-reward-label">Participation Reward</InputLabel>
                        <Select
                            labelId="event-reward-label"
                            id="event-reward"
                            name="participationReward"
                            value={formData.participationReward ?? ''}
                            onChange={handleRewardsChange}
                            label="Participation Reward"
                        >
                            <MenuItem value="" disabled>Select Reward</MenuItem>
                            {REWARD_OPTIONS.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {error && <FormHelperText>Please select a reward.</FormHelperText>}
                    </FormControl>
                </Grid>

                {/* Conditional Rendering: Show Custom Disc Fields */}
                {formData.participationReward === 2 && (
                    <>

                        {/* Mould Name Dropdown */}
                        <Grid item xs={12}>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ pt: 1 }}><Overline>Disc Mould</Overline></Box>
                                <FormControl fullWidth required margin="normal">
                                    <InputLabel id="mould-name-label">Mould Name</InputLabel>
                                    <Select
                                        labelId="mould-name-label"
                                        id="mould-name"
                                        name="mouldName"
                                        value={formData.customReward?.mouldName || 0}
                                        onChange={(e) => handleCustomDiscChange('mouldName', Number(e.target.value))}
                                        label="Mould Name"
                                    >
                                        {sortedMouldNames.map((mould) => (
                                            <MenuItem key={mould.value} value={mould.value}>
                                                {mould.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Disc Properties (Up to 2) */}
                        <Grid item xs={12}>
                            <Grid item xs={12}>
                                <Box sx={{ pt: 1 }}><Overline>Disc Properties</Overline></Box>
                            </Grid>

                            <Grid container spacing={2}>
                                {selectedDiscProperties.map((discProp, index) => (
                                    <Grid key={index} item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id={`disc-property-${index}-label`}>Property {index + 1}</InputLabel>
                                            <Select
                                                labelId={`disc-property-${index}-label`}
                                                id={`disc-property-${index}`}
                                                value={discProp}
                                                label={`Property ${index + 1}`}
                                                onChange={(e) => handleDiscPropertyChange(index, e.target.value)}
                                            >
                                                {sortedDiscPropertyTypes.map((type) => (
                                                    <MenuItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Buttons to Add/Remove Disc Properties */}
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth margin="normal">
                                        <Button
                                            variant="outlined"
                                            onClick={addDiscProperty}
                                            disabled={selectedDiscProperties.length >= 2}
                                        >
                                            Add Property
                                        </Button>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth margin="normal">
                                        <Button
                                            variant="outlined"
                                            onClick={removeDiscProperty}
                                            disabled={selectedDiscProperties.length === 0}
                                        >
                                            Remove Property
                                        </Button>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {customDiscError && selectedDiscProperties.length === 0 && (
                                <FormHelperText error>Please select at least one disc property.</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ pt: 1 }}><Overline>Disc Color</Overline></Box>
                        </Grid>

                        {/* Colors Name Autocomplete */}
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                freeSolo
                                options={sortedDiscColors}
                                value={formData.customReward?.colorsName || ''}
                                onChange={(event, newValue) => {
                                    handleCustomDiscChange('colorsName', newValue || '');
                                }}
                                onInputChange={(event, newInputValue) => {
                                    handleCustomDiscChange('colorsName', newInputValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Colors Name"
                                        variant="outlined"
                                        required
                                        error={customDiscError && (formData.customReward?.colorsName.trim() === '')}
                                        helperText={customDiscError && (formData.customReward?.colorsName.trim() === '') ? "Please select or enter a color name." : ""}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Color Stamp Checkbox */}
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.customReward?.cS || false}
                                        onChange={(e) => handleCustomDiscChange('cS', e.target.checked)}
                                        name="cS"
                                        color="primary"
                                    />
                                }
                                label={
                                    <>
                                        Use full color stamp <HelpTooltip label="Discs without full color will appear washed out" />
                                    </>
                                }
                            />
                        </Grid>

                        {/* Stamp Variant Input */}
                        <Grid item xs={12}>
                            <Grid item xs={12}>
                                <Box sx={{ pt: 1 }}><Overline>Disc Stamp</Overline></Box>
                            </Grid>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel id="stamp-type">Stamp Type</InputLabel>
                                        <Select
                                            labelId="stamp-type"
                                            value={stampType}
                                            label="Stamp Type"
                                            onChange={(e) => {
                                                const stampType = e.target.value;
                                                const numericValue = typeof stampType === 'string' ? Number(stampType) : stampType;

                                                setStampType(numericValue);
                                            }}
                                        >
                                            {DISC_STAMP_TYPES.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {
                                                stampType === 0 ?
                                                    'Stock stamps use the color selected above.'
                                                : stampType === 1 ?
                                                    <>
                                                        <span>Built-in stamps are stored in the game code. You can use <Link href="https://www.globaldiscgolf.club/colors/" rel="noopener" target="_blank">GDG Colors</Link> as a reference for stamps.</span>
                                                    </>
                                                : stampType === 2 ?
                                                    <>
                                                        <span>Firebase stamps are uploaded to the storage server and is where discs like DOTW are stored. You can use <Link href="https://www.globaldiscgolf.club/colors/" rel="noopener" target="_blank">GDG Colors</Link> as a reference for stamps.</span>
                                                    </>
                                                :
                                                    null
                                            }
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {
                                    stampType === 0 ?

                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth margin="normal">
                                                <TextField
                                                    label="Stock Variant"
                                                    helperText="Stock stamps from players may have different stock variations. 1 is most common for these stamp types while some others may have multiple versions available."
                                                    type="number"
                                                    fullWidth
                                                    value={formData.customReward?.stockVariant || 0}
                                                    onChange={(e) => handleCustomDiscChange('stockVariant', Number(e.target.value))}
                                                    inputProps={{ step: 1 }}
                                                />
                                            </FormControl>
                                        </Grid>

                                    : stampType === 1 ?

                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth margin="normal">
                                                <InputLabel id="stamp-name">Stamp Name</InputLabel>
                                                <Select
                                                    labelId="stamp-name"
                                                    value={formData.customReward?.stampVariant || 1}
                                                    label="Stamp Name"
                                                    onChange={(e) => handleCustomDiscChange('stampVariant', Number(e.target.value))}
                                                >
                                                    {sortedDiscStampNames.map((type) => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                    : stampType === 2 ?

                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth margin="normal">
                                                <TextField
                                                    label="Firebase Stamp Variant"
                                                    helperText={firebaseStampDraft
                                                        ? `Saved as -${firebaseStampDraft} (negative = Firebase stamp)`
                                                        : 'Enter the Firebase stamp number'}
                                                    error={!firebaseStampDraft}
                                                    fullWidth
                                                    value={firebaseStampDraft}
                                                    onChange={(e) => {
                                                        // Accept pasted "-125" too; the sign is always added on save
                                                        const digits = e.target.value.trim().replace(/^-/, '');
                                                        if (!/^\d*$/.test(digits)) return;
                                                        setFirebaseStampDraft(digits);
                                                        handleCustomDiscChange('stampVariant', draftToFirebaseStamp(digits));
                                                    }}
                                                    InputProps={{ startAdornment: <InputAdornment position="start">-</InputAdornment> }}
                                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                />
                                            </FormControl>
                                        </Grid>

                                    :

                                        null
                                }
                            </Grid>
                        </Grid>
                    </>
                )}
        </Grid>
    );
};

export default EventRewardsForm;
